import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Calculator, CheckCircle2, AlertCircle } from "lucide-react";
import { sanitizeDecimal, parseTRNumber } from "@/lib/validators";

const fmt2 = (n) => Number(n ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt4 = (n) => Number(n ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 4, maximumFractionDigits: 4 });

export default function CashCount() {
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const [countedCash, setCountedCash] = useState("");
  const [countedGold, setCountedGold] = useState("");
  const [note, setNote] = useState("");
  const [result, setResult] = useState(null);

  const load = async () => {
    const [t, h] = await Promise.all([api.get("/cashcount/today"), api.get("/cashcount/history")]);
    setToday(t.data);
    setHistory(h.data);
  };
  useEffect(() => { load(); }, []);

  const close = async () => {
    if (!countedCash) return toast.error("Sayılan nakit gerekli");
    const cash = parseTRNumber(countedCash);
    if (cash <= 0) return toast.error("Nakit tutarı geçerli bir sayı olmalı");
    try {
      const r = await api.post("/cashcount/close", {
        counted_cash: cash,
        counted_gold_gram: parseTRNumber(countedGold || 0),
        note,
      });
      setResult(r.data);
      toast.success("Gün sonu kapatıldı");
      load();
    } catch (e) { toast.error("Hata"); }
  };

  return (
    <div className="space-y-8" data-testid="cashcount-page">
      <div>
        <div className="label-caps">Gün Sonu</div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2 font-light">Kasa Sayımı</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-flat p-8">
          <div className="label-caps mb-2">Beklenen Değerler</div>
          <div className="font-display text-2xl mb-6">Sistem Kaydı</div>
          {today && (
            <div className="space-y-3">
              <Row lbl="Beklenen Nakit" val={`${fmt2(today.expected_cash)} ₺`} gold />
              <Row lbl="Beklenen Has" val={`${fmt4(today.expected_gold_gram)} gr`} />
              <Row lbl="İşlem Sayısı" val={today.transaction_count} />
            </div>
          )}
          <div className="border-t border-white/5 mt-6 pt-6">
            <div className="label-caps mb-4">Fiili Sayım</div>
            <div className="space-y-3">
              <div>
                <label className="label-caps block mb-1">Sayılan Nakit (₺)</label>
                <input type="text" inputMode="decimal" value={countedCash} onChange={(e)=>setCountedCash(sanitizeDecimal(e.target.value, { maxDecimals: 2 }))} placeholder="0,00" className="w-full bg-transparent border border-white/10 px-3 py-3 font-mono focus:outline-none focus:ring-1 focus:ring-[--gold]" data-testid="counted-cash" />
              </div>
              <div>
                <label className="label-caps block mb-1">Sayılan Has (gr)</label>
                <input type="text" inputMode="decimal" value={countedGold} onChange={(e)=>setCountedGold(sanitizeDecimal(e.target.value))} placeholder="0,0000" className="w-full bg-transparent border border-white/10 px-3 py-3 font-mono focus:outline-none focus:ring-1 focus:ring-[--gold]" data-testid="counted-gold" />
              </div>
              <input placeholder="Not (ops.)" value={note} onChange={(e)=>setNote(e.target.value)} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" />
            </div>
            <button onClick={close} className="btn-primary-gold w-full mt-4 flex items-center justify-center gap-2" data-testid="close-day"><Calculator size={14} /> Gün Sonunu Kapat</button>
          </div>
        </div>

        <div className="card-flat p-8">
          {result ? (
            <div>
              <div className={`flex items-center gap-3 mb-6`}>
                {result.verdict === "denk" ? <CheckCircle2 size={32} style={{color:"var(--gold)"}} /> : <AlertCircle size={32} className="text-red-400" />}
                <div>
                  <div className="font-display text-3xl" style={result.verdict==="denk"?{color:"var(--gold)"}:{color:"#EF4444"}}>{result.verdict === "denk" ? "DENK" : result.verdict === "acik" ? "AÇIK" : "FAZLA"}</div>
                  <div className="label-caps">Sayım Sonucu</div>
                </div>
              </div>
              <Row lbl="Nakit Farkı" val={`${result.cash_diff >= 0 ? "+" : ""}${fmt2(result.cash_diff)} ₺`} color={result.cash_diff>=0?"text-emerald-400":"text-red-400"} />
              <Row lbl="Has Farkı" val={`${fmt4(result.gold_diff)} gr`} color={Math.abs(result.gold_diff)<0.01?"text-emerald-400":"text-red-400"} />
              <button onClick={()=>setResult(null)} className="btn-gold mt-6" data-testid="new-count">Yeni Sayım</button>
            </div>
          ) : (
            <>
              <div className="label-caps mb-2">Son Sayımlar</div>
              <div className="font-display text-2xl mb-4">Geçmiş</div>
              {history.length === 0 && <div className="text-sm text-neutral-500">Kayıt yok</div>}
              <div className="max-h-96 overflow-auto divide-y divide-white/5">
                {history.map(h => (
                  <div key={h.id} className="py-3">
                    <div className="flex justify-between items-baseline">
                      <div className="font-mono text-sm">{h.date}</div>
                      <div className={`label-caps ${h.verdict==="denk"?"text-emerald-400":"text-red-400"}`}>{h.verdict.toUpperCase()}</div>
                    </div>
                    <div className="flex justify-between text-xs text-neutral-500 mt-1 font-mono">
                      <span>Nakit farkı: {fmt2(h.cash_diff)} ₺</span>
                      <span>Has farkı: {fmt4(h.gold_diff)} gr</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ lbl, val, gold, color }) {
  return (
    <div className="flex justify-between items-baseline py-3 border-b border-white/5">
      <span className="label-caps">{lbl}</span>
      <span className={`font-mono tabular-nums ${gold?"text-lg":""} ${color||""}`} style={gold?{color:"var(--gold)"}:{}}>{val}</span>
    </div>
  );
}
